import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Formik, Form, FormikHelpers } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import * as Sentry from "@sentry/react";
import EmployerUser from "../../../../apiResources/EmployerUser";
import Employer from "../../../../apiResources/Employer";
import EmailDomain from "../../../../apiResources/EmailDomain";
import SettingsLayout from "../../../layouts/SettingsLayout";
import SettingsHeading from "../../../settings/SettingsHeading";
import Loader from "../../../library/Loader";
import { PlainTextInputField, FieldErrorMessage } from "../../../library/Form";
import SubmitButton from "../../../library/Form/SubmitButton";
import Button from "../../../library/Button";
import Divider from "../../../library/Divider";
import Alert from "../../../library/Alert";

type FormValues = {
  name: string;
};

const Domains = (props: Props) => {
  const { user, company } = props;
  const [domains, setDomains] = useState<EmailDomain[]>(null);
  const [editing, setEditing] = useState<string>(null);
  const [error, setError] = useState<string>("");

  const getEmailDomains = async () => {
    const domains = (await EmailDomain.all()).data;
    setDomains(domains);
  };

  useEffect(() => {
    getEmailDomains();
  }, []);

  const clearError = () => setError("");

  const createEmailDomain = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    clearError();

    try {
      const domain = new EmailDomain({
        name: values.name,
        companyId: Number(company.id),
      });

      const success = await domain.save();

      if (success) {
        setDomains([...domains, domain]);
        helpers.resetForm({});
      } else {
        helpers.setErrors({ name: domain.errors.name.fullMessage });
      }
    } catch (err) {
      Sentry.captureException(err);
      helpers.setErrors({ name: "Something went wrong adding the domain" });
    }
  };

  const updateEmailDomain = async (
    domain: EmailDomain,
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    clearError();

    try {
      domain.assignAttributes(values);

      const success = await domain.save();

      if (success) {
        setEditing(null);
      } else {
        helpers.setErrors({ name: domain.errors.name.fullMessage });
      }
    } catch (err) {
      Sentry.captureException(err);
      helpers.setErrors({ name: "Something went wrong updating the domain" });
    } finally {
      domain.rollback();
    }
  };

  const deleteEmailDomain = async (domainToDelete: EmailDomain) => {
    clearError();

    try {
      const success = await domainToDelete.destroy();

      if (success) {
        setDomains(domains.filter((domain) => domain.id !== domainToDelete.id));
      }
    } catch (err) {
      Sentry.captureException(err);
      setError("Something went wrong deleting the domain");
    }
  };

  if (!user || !company || !domains) {
    return (
      <SettingsLayout user={user} company={company}>
        <Loader />
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout user={user} company={company}>
      <SettingsHeading>Email Domains</SettingsHeading>

      {domains.length === 0 && (
        <Alert status="info">
          An email domain is required to enable your &quot;forward to Hiring
          Managers&quot; feature. This allows you to email secure links to your
          colleagues so that they can easily review the applicants without
          needing a full Hiring Hub account. Adding domains here will enable the
          &quot;forward to Hiring Manager&quot; button on the candidate&apos;s
          application.
        </Alert>
      )}

      {error && <Alert status="error">{error}</Alert>}

      {domains.length > 0 && (
        <DomainList>
          {domains.map((domain) => (
            <React.Fragment key={domain.id}>
              {editing === domain.id ? (
                <EmailDomainInput
                  onSave={(values, helpers) =>
                    updateEmailDomain(domain, values, helpers)
                  }
                  onCancel={() => setEditing(null)}
                  value={domain.name}
                />
              ) : (
                <DomainRow>
                  <FontAwesomeIcon icon={faAt} />
                  <DomainName>{domain.name}</DomainName>
                  {user.isAdmin && (
                    <>
                      <IconButton
                        type="button"
                        onClick={() => setEditing(domain.id)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </IconButton>
                      <IconButton
                        type="button"
                        onClick={() => deleteEmailDomain(domain)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </IconButton>
                    </>
                  )}
                </DomainRow>
              )}
              <Divider size="sm" />
            </React.Fragment>
          ))}
        </DomainList>
      )}

      {user.isAdmin ? (
        <>
          <SubHeading>Add an email domain</SubHeading>
          <DomainRow>
            <EmailDomainInput onSave={createEmailDomain} />
          </DomainRow>
        </>
      ) : (
        <p>
          Talk to the administrator of your hiring hub account to add email
          domains.
        </p>
      )}
    </SettingsLayout>
  );
};

const EmailDomainInput = (props) => {
  const { onSave, onCancel, value = "" } = props;

  return (
    <Formik
      onSubmit={onSave}
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={{
        name: value,
      }}
      validationSchema={Yup.object({
        name: Yup.string().required("Please enter a domain name"),
      })}
    >
      <EmailDomainForm autoComplete="off">
        <DomainRow>
          <FontAwesomeIcon icon={faAt} />
          <InputWrapper>
            <PlainTextInputField name="name" placeholder="hiring-hub.com" />
          </InputWrapper>
          <ButtonGroup>
            <SubmitButton size="sm" />
            {onCancel && (
              <Button size="sm" type="button" tertiary onClick={onCancel}>
                Cancel
              </Button>
            )}
          </ButtonGroup>
        </DomainRow>
        <FieldErrorMessage name="name" />
      </EmailDomainForm>
    </Formik>
  );
};

type Props = {
  user: EmployerUser;
  company: Employer;
};

const IconButton = styled.button`
  appearance: none;
  border: none;
  background: none;
`;

const DomainRow = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.muted};
`;

const DomainList = styled.div`
  margin-bottom: 2rem;
`;

const DomainName = styled.div`
  flex: 1;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const EmailDomainForm = styled(Form)`
  width: 100%;
`;

const InputWrapper = styled.div`
  flex: 1;
  padding-left: 0.5rem;
  padding-right: 1rem;
`;

const SubHeading = styled.h3`
  margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
  > button {
    margin-left: 0.5rem;
    margin-right: 0.5rem;

    &:first-child {
      margin-left: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }
`;

export default Domains;
